import React from 'react'
import { withRouter } from 'react-router-dom'
import styled from 'styled-components'

import Button from 'components/lib/DesignSystem/Button'
import { Close } from 'components/lib/DesignSystem/Icons'
import PopupMessage from 'components/lib/DesignSystem/PopupMessage'

const mapBackgroundColorByType = {
  info: 'var(--links-color)',
  neutral: 'var(--color-elevated-solid)',
  error: 'var(--negative-color)',
  warning: 'var(--warning-color)',
  success: 'var(--success-fill-bg)',
}

class ToastsContainerItem extends React.Component {
  TIME_TO_DISAPPEAR = 5000

  mapImgByType = {
    info: `${STATIC_BASE_URL}/static/webapp/images/toast-info.svg`,
    error: `${STATIC_BASE_URL}/static/webapp/images/toast-error.svg`,
    success: `${STATIC_BASE_URL}/static/webapp/images/toast-info.svg`,
    warning: `${STATIC_BASE_URL}/static/webapp/images/toast-error.svg`,
  }

  componentDidMount() {
    this.scheduleHideTimeout()
    const modalCloseIcon = document.getElementById('x-modal_close')
    if (modalCloseIcon) modalCloseIcon.click()
  }

  componentWillUnmount() {
    clearInterval(this.timerId)
  }

  scheduleHideTimeout = () => {
    const { timeToDisappear } = this.props
    this.timerId = setTimeout(() => {
      if (document.hidden) this.scheduleHideTimeout()
      else this.props.removeToast(this.props.nId)
    }, timeToDisappear || this.TIME_TO_DISAPPEAR)
  }

  handlerOnClick = () => {
    const { nId, removeToast } = this.props
    removeToast(nId)
  }

  renderIcon = () => {
    const { icon: Icon, type } = this.props
    if (Icon)
      return (
        <div className="icon-container">
          <Icon with="22px" height="22px" />
        </div>
      )
    return this.mapImgByType[type] && <img src={this.mapImgByType[type]} alt={type} />
  }

  render() {
    const { className, message, title = '', type, button, id } = this.props
    return (
      <div id={id} className={className}>
        <div className="img-container mr">{this.renderIcon()}</div>
        <div className="toasts-container">
          <Close
            width="22px"
            height="22px"
            onClick={this.handlerOnClick}
            style={{ position: 'absolute', right: '0' }}
            fill={type === 'neutral' ? 'var(--ds-blue-1)' : '#fff'}
          />
          {title && <span className="title-container">{title}</span>}
          <span className="message-container">{message}</span>
          {button && (
            <Button
              id={button.id}
              onClick={() => {
                if (typeof button?.onClick === 'function') {
                  button.onClick()
                }
                this.handlerOnClick()
              }}
              text={button?.text}
              style={{
                lineHeight: '14px',
                background: 'transparent',
                border: type === 'neutral' ? 'none' : '1px solid #fff',
                borderRadius: '4px',
                color: type === 'neutral' ? 'var(--primary)' : '#fff',
                ...(type === 'neutral' && { padding: '0' }),
              }}
            />
          )}
        </div>
      </div>
    )
  }
}

// Rich, self-rendered toast (e.g. the credit-request card or the credits-restored
// toast). `persistent` toasts own their own dismissal and render raw; non-persistent
// ones get an injected `onClose` and auto-dismiss after `timeToDisappear` (default 5s).
function ComponentToastItem({ elem, removeToast }) {
  const close = React.useCallback(() => removeToast(elem.id), [elem.id, removeToast])
  React.useEffect(() => {
    if (elem.persistent) return undefined
    const timer = setTimeout(close, elem.timeToDisappear || 5000)
    return () => clearTimeout(timer)
  }, [elem.persistent, elem.timeToDisappear, close])

  if (elem.persistent) return elem.component
  return React.cloneElement(elem.component, { onClose: elem.component.props.onClose || close })
}

function ToastsContainer({ className, toasts, removeToast, history, dispatch }) {
  if (!toasts.length) return ''

  return (
    <div className={className}>
      {toasts.map((elem) => (
        <React.Fragment key={elem.id}>
          {elem.component ? (
            <ComponentToastItem elem={elem} removeToast={removeToast} />
          ) : elem.ds ? (
            <PopupMessage
              key={elem.id}
              className={elem.className}
              cover={elem.cover}
              title={elem.title}
              message={elem.message}
              boldInMessage={elem.boldInMessage}
              onClose={() => removeToast(elem.id)}
              ctaText={elem.ctaText}
              ctaOnClick={() => {
                // Support custom Redux actions
                if (elem.ctaAction && dispatch) {
                  dispatch(elem.ctaAction)
                }
                // Support URL navigation
                if (elem.ctaInternalRedirect) {
                  history.push(elem.ctaInternalRedirect)
                }
                removeToast(elem.id)
              }}
              type={elem.type}
            />
          ) : (
            <StyledToastsContainerItem
              key={elem.id}
              id={elem.id}
              nId={elem.id}
              title={elem.title}
              message={elem.message}
              icon={elem.icon}
              button={elem.button}
              removeToast={removeToast}
              timeToDisappear={elem.timeToDisappear}
              type={elem.type || 'success'}
            />
          )}
        </React.Fragment>
      ))}
    </div>
  )
}

const StyledToastsContainer = styled(ToastsContainer)`
  position: fixed;
  right: 40px;
  bottom: 40px;
  display: flex;
  flex-direction: column-reverse;
  align-items: flex-end;
  z-index: 1005;
  & > *:not(:first-child) {
    margin-bottom: 20px;
  }
`

const StyledToastsContainerItem = styled(ToastsContainerItem)`
  width: ${(props) => props.width || '360px'};
  padding: 15px 15px 15px 52px;
  border-radius: 6px;
  background-color: ${(props) => mapBackgroundColorByType[props.type]};
  box-shadow: var(--shadow-1);
  transition: 0.3s ease-out;
  position: relative;
  ${({ type }) =>
    type === 'neutral' &&
    `
  border: 2px solid var(--primary);
  border-radius: 14px;
  .title-container {
    color: var(--ds-blue-1);
    font-weight: 700;
    margin-bottom: 20px;
  }
    svg path {
      fill: var(--primary);
    }
  `}
  img,
  .icon-container {
    width: 22px;
    margin-right: 15px;
    position: absolute;
    top: 15px;
    left: 15px;
  }
  .toasts-container {
    color: ${(props) => (props.type === 'neutral' ? 'var(--color-text)' : '#fff')};
    padding: 0;
    position: relative;
    word-wrap: break-word;
    & > * {
      display: inline-block;
      vertical-align: middle;
      &.tm-icon {
        position: absolute;
        top: 0;
        right: 0;
      }
    }
    .img-container {
      width: 20px;
    }
    .message-container {
      width: calc(100% - 55px);
    }
  }
`

export default withRouter(StyledToastsContainer)
